import { player, team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import { posRatings } from "../../common/posRatings.ts";
import type { PlayerRatingKey, PlayerStatAttr } from "../../common/types.ts";
import { defineView } from "../util/defineView.ts";
import { bySport } from "../../common/sportFunctions.ts";
import {
	NUM_LINES,
	NUM_PLAYERS_PER_LINE,
} from "../../common/constants.hockey.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import type { RouteParams } from "../../ui/router/types.ts";
import { helpers } from "../util/index.ts";
import { validateAbbrev } from "../util/processInputs.ts";
import { validateSeasonType } from "../util/processInputs.ts";

const processInputs = (params: RouteParams<"depth">) => {
	// Fix broken links
	if (params.abbrev === "FA" || params.abbrev === "FA_-1") {
		// https://stackoverflow.com/a/59923262/786644
		const returnValue = {
			redirectUrl: helpers.leagueUrl(["free_agents"]),
		};
		return returnValue;
	}

	if (params.abbrev === "DP" || params.abbrev === "DP_-2") {
		// https://stackoverflow.com/a/59923262/786644
		const returnValue = {
			redirectUrl: helpers.leagueUrl(["draft_scouting"]),
		};
		return returnValue;
	}

	const [tid, abbrev] = validateAbbrev(params.abbrev);

	const DEFAULT_POS = bySport({
		baseball: "L",
		basketball: "G",
		football: "QB",
		hockey: "F",
	});

	const pos = params.pos ?? DEFAULT_POS;

	return { abbrev, playoffs: validateSeasonType(params.playoffs), pos, tid };
};

const defenseStats: PlayerStatAttr[] = [
	"defTckSolo",
	"defTckAst",
	"defTck",
	"defTckLoss",
	"defSk",
	"defSft",
	"defPssDef",
	"defInt",
	"defIntYds",
	"defIntTD",
	"defIntLng",
	"defFmbFrc",
	"defFmbRec",
	"defFmbYds",
	"defFmbTD",
];

const baseballLineupStats: PlayerStatAttr[] = [
	"war",
	"ab",
	"h",
	"hr",
	"ba",
	"r",
	"rbi",
	"sb",
	"obp",
	"slg",
	"ops",
];

export const buffOvrDH = (p: {
	ratings: {
		ovrs: {
			DH: number;
		};
		pots: {
			DH: number;
		};
	};
}) => {
	// For roster auto sort to work, it's best if DH is only the offensive component, so it is directly comparable to other positions. Otherwise you could wind up with a better offensive+defensive player winding up at DH, when you'd rather have him in the field. But then, displayed ovrs for DHs are really low. So adjust it with this.
	for (const key of ["ovrs", "pots"] as const) {
		if (p.ratings?.[key]?.DH !== undefined) {
			p.ratings[key].DH = player.limitRating((0.95 / 0.7) * p.ratings[key].DH);
		}
	}
};

const stats = bySport<Record<string, PlayerStatAttr[]>>({
	baseball: {
		L: baseballLineupStats,
		LP: baseballLineupStats,
		D: baseballLineupStats,
		DP: baseballLineupStats,
		P: ["war", "w", "l", "era", "gpPit", "gsPit", "sv", "ip", "soPit", "whip"],
	},
	basketball: {},
	football: {
		QB: [
			"pssCmp",
			"pss",
			"cmpPct",
			"pssYds",
			"pssTD",
			"pssInt",
			"pssSk",
			"pssSkYds",
			"qbRat",
			"fmbLost",
		],
		RB: [
			"rus",
			"rusYds",
			"rusYdsPerAtt",
			"rusLng",
			"rusTD",
			"tgt",
			"rec",
			"recYds",
			"recYdsPerRec",
			"recTD",
			"recLng",
			"fmbLost",
		],
		WR: ["tgt", "rec", "recYds", "recYdsPerRec", "recTD", "recLng", "fmbLost"],
		TE: ["tgt", "rec", "recYds", "recYdsPerRec", "recTD", "recLng", "fmbLost"],
		OL: ["pbw", "pba", "pbwr", "rbw", "rba", "rbwr", "skAlw", "skAlwPct"],
		DL: defenseStats,
		LB: defenseStats,
		CB: defenseStats,
		S: defenseStats,
		K: [
			"fg",
			"fga",
			"fgPct",
			"fgLng",
			"xp",
			"xpa",
			"xpPct",
			"kickingPts",
			"ko",
			"koYds",
			"koYdsPerAtt",
			"koTB",
			"koTBPct",
			"ok",
			"okRec",
			"okRecPct",
		],
		P: [
			"pnt",
			"pntYdsPerAtt",
			"pntLng",
			"pntTB",
			"pntTBPct",
			"pntIn20",
			"pntIn20Pct",
			"pntBlk",
		],
		KR: ["kr", "krYds", "krYdsPerAtt", "krLng", "krTD"],
		PR: ["pr", "prYds", "prYdsPerAtt", "prLng", "prTD"],
	},
	hockey: {
		F: ["gp", "amin", "g", "a", "ops", "dps", "ps"],
		D: ["gp", "amin", "g", "a", "ops", "dps", "ps"],
		G: ["gp", "gaa", "svPct", "gps"],
	},
});

export default defineView({
	id: "depth",
	processInputs,
	load: async ({
		inputs: { abbrev, playoffs, pos, tid },
		updateEvents,
		prevInputs,
	}) => {
		if (
			__SPORT !== "baseball" &&
			__SPORT !== "football" &&
			__SPORT !== "hockey"
		) {
			throw new Error("Not implemented");
		}

		if (
			updateEvents.has("firstRun") ||
			updateEvents.has("gameSim") ||
			updateEvents.has("playerMovement") ||
			updateEvents.has("gameAttributes") ||
			updateEvents.has("team") ||
			pos !== prevInputs?.pos ||
			playoffs !== prevInputs?.playoffs ||
			abbrev !== prevInputs?.abbrev
		) {
			let showDH: "noDH" | "dh" | "both" | undefined;
			let pos2 = pos;
			if (__SPORT === "baseball") {
				const dh = g.get("dh");
				if (dh === "none") {
					showDH = "noDH";
				} else if (dh === "all") {
					showDH = "dh";
				} else {
					const confs = g.get("confs");
					const filteredConfs = confs.filter((conf) => dh.includes(conf.cid));
					if (confs.length === filteredConfs.length) {
						showDH = "dh";
					} else if (filteredConfs.length === 0) {
						showDH = "noDH";
					} else {
						showDH = "both";
					}
				}

				if (showDH === "noDH") {
					if (pos === "L") {
						pos2 = "LP";
					} else if (pos === "D") {
						pos2 = "DP";
					}
				} else if (showDH === "dh") {
					if (pos === "LP") {
						pos2 = "L";
					} else if (pos === "DP") {
						pos2 = "D";
					}
				}
			}

			const editable = tid === g.get("userTid") && !g.get("spectator");
			const ratings: PlayerRatingKey[] = [
				...(__SPORT === "baseball"
					? pos2 === "P"
						? []
						: (["hgt", "spd"] as const)
					: (["hgt", "stre", "spd", "endu"] as const)),
				...posRatings(pos2),
			];
			const playersAll = await idb.cache.players.indexGetAll(
				"playersByTid",
				tid,
			);
			const players = addFirstNameShort(
				await player.formatAll(playersAll, {
					attrs: ["pid", "firstName", "lastName", "age", "injury", "watch"],
					ratings: ["skills", "pos", "ovr", "pot", "ovrs", "pots", ...ratings],
					seasonType: playoffs,
					stats: [...stats[pos2]!, "jerseyNumber"],
					season: g.get("season"),
					showNoStats: true,
					showRookies: true,
					fuzz: true,
				}),
			);

			// Sort players based on current depth chart
			const t = await idb.cache.teams.get(tid);

			if (!t || !t.depth) {
				throw new Error("Missing depth");
			}

			const depthPlayers = team.getDepthPlayers(t.depth, players);

			const stats2: string[] = stats[pos2] ?? [];

			const players2: any[] = depthPlayers[pos2] ?? [];

			let multiplePositionsWarning: string | undefined;
			if (__SPORT === "hockey" && players.length >= g.get("minRosterSize")) {
				const playerInfoByPid = new Map<
					number,
					{
						name: string;
						positions: string[];
					}
				>();

				for (const [pos, posPlayers] of Object.entries(depthPlayers)) {
					const numStarters =
						NUM_LINES[pos as keyof typeof NUM_LINES] *
						NUM_PLAYERS_PER_LINE[pos as keyof typeof NUM_PLAYERS_PER_LINE];

					for (let i = 0; i < numStarters; i++) {
						const p = posPlayers[i];
						if (!p) {
							break;
						}

						const { firstName, lastName, pid } = p;
						let info = playerInfoByPid.get(pid);
						if (!info) {
							info = {
								name: `${firstName} ${lastName}`,
								positions: [],
							};
							playerInfoByPid.set(pid, info);
						}
						info.positions.push(pos);
					}
				}

				const playersAtMultiplePositions = [];
				for (const { name, positions } of playerInfoByPid.values()) {
					if (positions.length > 1) {
						playersAtMultiplePositions.push(`${name} (${positions.join("/")})`);
					}
				}

				if (playersAtMultiplePositions.length > 0) {
					multiplePositionsWarning = `Some players are in the rotation at multiple positions, which may lead to erratic substitution patterns: ${playersAtMultiplePositions.join(
						", ",
					)}.`;
				}
			}

			if (__SPORT === "baseball") {
				for (const p of players2) {
					buffOvrDH(p);
				}
			}

			return {
				abbrev,
				editable,
				keepRosterSorted: t.keepRosterSorted,
				multiplePositionsWarning,
				pos: pos2,
				players: players2,
				playoffs,
				ratings,
				showDH,
				stats: stats2,
				tid,
			};
		}
	},
});
